import { FONT_MONO } from '../../config/fonts';
import { useEffect, useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, alpha, useTheme,
} from '@mui/material'
import { ContentCopy, Lock } from '@mui/icons-material'
import copy from 'copy-to-clipboard'
import { useCall } from '../../context/call'
import { formatPasscode } from '../../utils/e2ee'

// Two dialogs for private (end-to-end encrypted) meetings:
//  - a joiner has to type the passcode
//  - the host sees the passcode to share it
const E2EEPasscodeDialog = () => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const accent = theme.palette.secondary.main
  const { passcodePrompt, submitPasscode, cancelPasscode, passcodeShare, dismissPasscodeShare } = useCall()

  const [text, setText] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (passcodePrompt) setText('')
  }, [passcodePrompt?.info?.callId])        // eslint-disable-line react-hooks/exhaustive-deps

  const paperSx = {
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.text.primary,
    borderRadius: 2,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
    boxShadow: 8,
    overflow: 'hidden',
  }

  const titleSx = { px: 2, py: 1.5, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }
  const goldSx = {
    backgroundColor: accent,
    color: theme.palette.getContrastText(accent),
    fontWeight: 700,
    borderRadius: 1.5,
    boxShadow: 'none',
    '&:hover': { backgroundColor: theme.palette.secondary.light, boxShadow: 'none' },
    '&.Mui-disabled': { backgroundColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)' },
  }

  return (
    <>
      {/* Joiner: enter the passcode */}
      <Dialog
        open={Boolean(passcodePrompt)}
        onClose={cancelPasscode}
        fullWidth
        maxWidth="xs"
        sx={{ zIndex: theme.zIndex.modal + 60 }}
        PaperProps={{ sx: paperSx }}
      >
        <DialogTitle sx={titleSx}>
          <Box display="flex" alignItems="center" gap={1}>
            <Lock sx={{ color: accent }} />
            Private meeting
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1.75, backgroundColor: theme.palette.background.alt }}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 1.5 }}>
            {passcodePrompt?.info?.groupName ? `“${passcodePrompt.info.groupName}” is` : 'This meeting is'} end-to-end
            encrypted. Enter the passcode you received from the host.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && text.trim()) submitPasscode(text)
            }}
            error={Boolean(passcodePrompt?.error)}
            helperText={passcodePrompt?.error || ' '}
            inputProps={{ style: { fontFamily: FONT_MONO, letterSpacing: 1.5 } }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={cancelPasscode} sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!text.trim()} onClick={() => submitPasscode(text)} sx={goldSx}>
            Join
          </Button>
        </DialogActions>
      </Dialog>

      {/* Host: share the passcode */}
      <Dialog
        open={Boolean(passcodeShare)}
        onClose={dismissPasscodeShare}
        fullWidth
        maxWidth="xs"
        sx={{ zIndex: theme.zIndex.modal + 60 }}
        PaperProps={{ sx: paperSx }}
      >
        <DialogTitle sx={titleSx}>
          <Box display="flex" alignItems="center" gap={1}>
            <Lock sx={{ color: accent }} />
            Share the passcode
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1.75, backgroundColor: theme.palette.background.alt }}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Give this passcode to the people you want in the meeting. Nobody without it can hear or see the meeting,
            not even the server.
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              textAlign: 'center',
              borderRadius: 1.5,
              border: `1px solid ${alpha(accent, 0.6)}`,
              backgroundColor: alpha(accent, 0.08),
            }}
          >
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '1.25rem', fontWeight: 700, letterSpacing: 2 }}>
              {formatPasscode(passcodeShare)}
            </Typography>
          </Box>

          <Typography variant="caption" color="error" display="block" sx={{ mt: 1.5 }}>
            Do not send it in this app's chat: the server can read chat messages. Use a phone call or tell people in
            person.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.25, gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button
            startIcon={<ContentCopy />}
            onClick={() => {
              copy(formatPasscode(passcodeShare))
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
            sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button variant="contained" onClick={dismissPasscodeShare} sx={goldSx}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default E2EEPasscodeDialog